// Plan-BundlesImplementation §E.1 + §E.4 — "Top-Up Credits" section.
//
// Renders:
//  1. Three bundle cards: Text $5, Voice $7, LiveAvatar $25.
//     ($0.10 Test Bundle retired 2026-05-11 per CEO directive — test
//     workspaces use Stripe test-mode 4242 cards to buy the real SKUs.)
//  2. The "Active credits" panel (read-only table). Phase 2 backend
//     (Storm task #10) will populate `billing.active_credits[]` — we
//     surface it here whenever present, otherwise show an empty state.
//
// 🚨 THE EMPTY STATE IS CONDITIONAL, AND THAT IS A BUG FIX — DO NOT COLLAPSE
// IT BACK TO ONE LINE. A pooled Text Top-Up never creates a `workspace_credits`
// row, so a workspace holding a paid, positive top-up balance rendered
// "No active credits. Buy a bundle above to extend your allowance." — every
// word technically true of `workspace_credits`, and read by the owner as "the
// $10 I just paid vanished". The invariant this file now upholds:
//
//   THE BILLING PAGE MUST NEVER STATE OR IMPLY "no credits / buy a bundle"
//   WHILE THE WORKSPACE HOLDS A POSITIVE POOLED BALANCE.
//
// Upheld by surfacing `usage.text_tokens_topup` as a labelled callout above
// the table (NOT as a fabricated table row — see the RESOURCE_LABEL note) and
// by swapping the empty copy when that balance exists.
//
// Trial gate (§E.4): trial workspaces see the cards disabled with an
// upgrade tooltip. Owner-only gate per §B.7 — admins see the section
// but the purchase buttons are owner-only.

import React, { useState } from 'react';
import {
  MessageSquare,
  Mic,
  Video,
  Sparkles,
  Info,
  Wallet,
} from 'lucide-react';
import type { WorkspaceBilling, BundleType, ActiveCredit } from '../../types/billing';
import {
  isPooledBilling,
  TEXT_TOPUP_TOKENS,
  TEXT_TOPUP_TOTAL_USD,
} from '../../types/billing';
import { formatTokenCount } from './usageBars';
import TextBundleModal from './TextBundleModal';
import SimpleBundleModal, { type SimpleBundleKind } from './SimpleBundleModal';

interface Props {
  billing: WorkspaceBilling;
  workspaceId: string;
  isOwner: boolean;
  /** Active credit rows for the workspace, fetched separately by the
   *  parent BillingPage (Storm's contract makes `/api/billing/credits`
   *  a sibling endpoint of `/api/billing/workspace/{id}`). Empty array
   *  while loading or when the workspace has no credits. */
  credits: ActiveCredit[];
}

interface BundleCardSpec {
  kind: BundleType;
  title: string;
  price: string;
  oneLiner: string;
  icon: React.ReactNode;
  accent: string; // tailwind gradient
}

const BUNDLE_CARDS: BundleCardSpec[] = [
  {
    kind: 'text',
    title: 'Text Bundle',
    price: '$5',
    oneLiner: 'Allocate across chats, emails, knowledge, scrapes, reports.',
    icon: <MessageSquare className="w-5 h-5" />,
    accent: 'from-blue-500 to-indigo-600',
  },
  {
    kind: 'voice',
    title: 'Voice Bundle',
    price: '$7',
    oneLiner: '80 voice minutes at $0.0875/min.',
    icon: <Mic className="w-5 h-5" />,
    accent: 'from-amber-500 to-orange-600',
  },
  {
    kind: 'liveavatar',
    title: 'LiveAvatar Bundle',
    price: '$25',
    oneLiner: '50 LiveAvatar minutes. Fail-closed when exhausted.',
    icon: <Video className="w-5 h-5" />,
    accent: 'from-purple-500 to-pink-600',
  },
];

/** Pooled-mode replacement for the Text Bundle card. Same visual slot and
 *  same accent so the section does not visibly reshuffle at the cutover —
 *  what changes is the price, the quantity, and the absence of an
 *  allocation step. */
const TEXT_TOPUP_CARD: BundleCardSpec = {
  kind: 'text_topup',
  title: 'Text Top-Up',
  price: `$${TEXT_TOPUP_TOTAL_USD}`,
  oneLiner: `${formatTokenCount(TEXT_TOPUP_TOKENS)} AI text tokens, added straight to your pool. Never expires.`,
  icon: <MessageSquare className="w-5 h-5" />,
  accent: 'from-blue-500 to-indigo-600',
};

/** ⚠️ The `text_tokens` entries in this map and the two formatters below are
 *  DEFENSIVE, not load-bearing. The Active-credits table renders
 *  `workspace_credits` rows, and a pooled top-up never creates one —
 *  `grant_text_topup` writes `workspace_text_topups` and a Redis cycle field
 *  instead, by design. So a `text_tokens` row should never appear here today.
 *  Kept because the maps are exhaustive `Record`s over union types (a missing
 *  key is a compile error, not a blank cell) and because the union carries
 *  `text_tokens` for the usage payload regardless. Do not infer from these
 *  that top-ups show up in this table — they don't; the pool bar, the AI Text
 *  Top-Ups section, and the callout above this table are where a customer sees
 *  them. That callout is deliberately NOT a synthesised row in this table: the
 *  two systems are separate on purpose, a top-up has no `expires_at` and no
 *  `amount_initial` to put in those columns, and faking a row would re-create
 *  the same confusion one layer down in the data. */
const RESOURCE_LABEL: Record<ActiveCredit['resource'], string> = {
  text_tokens: 'AI text tokens',
  chat_conversations: 'Chats',
  email_threads: 'Emails',
  knowledge_summaries: 'Knowledge',
  web_scrapes: 'Web scrapes',
  reports: 'Reports',
  voice_minutes: 'Voice min',
  liveavatar_minutes: 'LiveAvatar min',
};

/** `bundle_type` was rendered with CSS `capitalize`, which turns the pooled
 *  SKU's snake_case value into "Text_topup". Map it explicitly instead. */
const BUNDLE_TYPE_LABEL: Record<BundleType, string> = {
  text: 'Text',
  text_topup: 'Text Top-Up',
  voice: 'Voice',
  liveavatar: 'LiveAvatar',
};

/** Token counts run to seven figures, where `toLocaleString()` ("2,000,000")
 *  is both wide and hard to scan next to the four-digit counts in the other
 *  rows. Compact only that one resource; every other row is unchanged. */
const formatCreditAmount = (c: ActiveCredit): string =>
  c.resource === 'text_tokens' ? formatTokenCount(c.amount) : c.amount.toLocaleString();

const formatCreditInitial = (c: ActiveCredit): string =>
  c.resource === 'text_tokens'
    ? formatTokenCount(c.amount_initial)
    : c.amount_initial.toLocaleString();

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const BundleCards: React.FC<Props> = ({ billing, workspaceId, isOwner, credits }) => {
  const isTrial = billing.subscription_status === 'trialing';

  const [activeModal, setActiveModal] = useState<BundleType | null>(null);

  // Plan-TextVoicePoolBilling-2026-08 §3 + §D — the legacy Text Bundle
  // allocates credits across the five retired per-category resources, so in
  // pooled mode it is SWAPPED for the flat Text Top-Up rather than merely
  // hidden. Voice and LiveAvatar bundles are unaffected — those pools did
  // not change.
  //
  // 🚨 The swap is payload-driven (`isPooledBilling`), not a client flag, and
  // the legacy branch below is untouched: a flag-off payload still renders
  // the $5 Text Bundle and its TextBundleModal, which remains the rollback
  // path. Nothing is deleted.
  const pooled = isPooledBilling(billing);

  // 🚨 The LiveAvatar bundle is an ACCESSORY of the add-on, not a standalone
  // product — `back c6ed12a` returns 403 without an active add-on (owner ruling
  // 2026-08-20: "a user cannot purchase the live avatar bundle if he has no
  // live avatar add on"). Until this gate the card disabled on `!isOwner`
  // ALONE, so a customer with no add-on could click Buy and have their first
  // signal be a 403 — and, post the same ruling, could have bought 50 minutes
  // they were structurally unable to spend.
  //
  // Mirrors `plan_guards.liveavatar_addon_is_active`: a tier AND an active
  // status. ⚠️ It CANNOT mirror the coupon branch — that predicate also denies
  // a `source: "coupon"` add-on past its `current_period_end`, and
  // `_build_liveavatar_addon_payload` does not emit `source`. The residual is
  // narrow and self-healing (the hourly sweep nulls the field, and the 403's
  // own message still surfaces as a toast), but an expired-coupon workspace can
  // still see an enabled button. Do NOT "fix" this by treating an elapsed
  // `current_period_end` as inactive for everyone: on a Stripe add-on that
  // field is the RENEWAL date, so a late webhook would disable the button for
  // customers who genuinely hold the add-on — stricter than the server, which
  // is the worse failure.
  //
  // ✅ RESIDUAL CLOSED (`back bc75d88`). The server now emits `can_buy_bundle` —
  // the DECISION, from the same `resolve_liveavatar_entitlement(...).active` the
  // purchase endpoint 403s on, so the button and the refusal cannot disagree.
  // That closes the expired-coupon window this predicate could not see: the
  // payload never carried `source`, and reconstructing the coupon deny from
  // `current_period_end` would have blocked every Stripe customer on a late
  // renewal webhook, because on a Stripe add-on that field is the RENEWAL date.
  //
  // The local predicate survives ONLY as the old-backend fallback, so a frontend
  // deployed ahead of `bc75d88` behaves exactly as it did before. Once the
  // backend is everywhere, the `??` arm is dead code and should be deleted
  // along with `avatarAddonActive`.
  const avatarAddon = billing.liveavatar_addon;
  const avatarAddonActive = Boolean(avatarAddon?.tier) && avatarAddon?.status === 'active';
  const canBuyAvatarBundle = avatarAddon?.can_buy_bundle ?? avatarAddonActive;
  const cards = pooled
    ? BUNDLE_CARDS.map((c) => (c.kind === 'text' ? TEXT_TOPUP_CARD : c))
    : BUNDLE_CARDS;

  const activeCredits = credits;

  // 🚨 REMAINING pooled top-up balance — the same field and the same number the
  // Top-Up Tokens meter in Monthly Usage renders
  // (`UsageSnapshot.text_tokens_topup`), never a second derivation. As of
  // 2026-08-17 that meter is a bar of its own rather than a term inside the
  // plan bar's denominator; this strip is the same balance said quietly, in the
  // panel that would otherwise deny it exists ("No active credits…"). Do not
  // delete it because a meter now exists — the meter is in a different card,
  // and the denial this fixes is right here.
  //
  // 🚨 THREE STATES, AND A `?? 0` WOULD COLLAPSE TWO OF THEM. Storm's
  // `strip_pool_keys_if_dark` (`back/api/billing.py:330`) OMITS this key rather
  // than zeroing it when `TEXT_POOL_BILLING_ENABLED` is off — omit-never-zero,
  // because `0` is a real state and absence is a different claim:
  //
  //   pooled === false                → NOT APPLICABLE. The key is absent, the
  //     $5 Text Bundle's credits genuinely belong in the table below, and its
  //     stock empty state is correct. `null` here, never `0`.
  //   pooled && typeof === 'number'   → APPLICABLE. `0` means a top-up that was
  //     bought and fully spent, or one never bought — indistinguishable from
  //     THIS field alone, which is why the zero-balance copy below stays the
  //     generic sentence (see there). `> 0` is money the customer is holding.
  //
  // The settings default is FALSE, so on a prod deploy the key is ABSENT, not
  // zero. Branching on `pooled` (which reads `text_pool_enabled` first, via
  // `isPooledBilling`) before touching the value is what keeps a missing key
  // from being read as "your balance is zero".
  const rawTopUpBalance = billing.usage?.text_tokens_topup;
  const topUpBalance: number | null =
    pooled && typeof rawTopUpBalance === 'number' ? rawTopUpBalance : null;
  const hasTopUpBalance = topUpBalance !== null && topUpBalance > 0;

  // E.4 trial gate: hide the buy buttons entirely. Show the section
  // with a clear upgrade CTA instead — keeps the page from feeling
  // empty for trial users on the Billing page.
  if (isTrial) {
    return (
      <section className="card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Top-Up Credits</h2>
          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
            Trial
          </span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Top-ups are available on paid plans.
        </p>
        <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-6 text-center">
          <Sparkles className="w-8 h-8 text-indigo-500 mx-auto mb-3" />
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-1 font-medium">
            Upgrade to unlock bundle purchases
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Once you're on a paid plan, you can buy top-up bundles to extend your
            monthly allowance.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Top-Up Credits</h2>
        {!isOwner && (
          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            Read-only
          </span>
        )}
      </div>
      {/* 🚨 THREE DIFFERENT LIFETIMES SHARE THIS GRID — the sentence below sits
          directly above cards it would otherwise misdescribe:
            - Voice / LiveAvatar → genuine 365-day credits, consumed oldest-first.
            - Legacy $5 Text Bundle (flag-off branch) → same 365-day credits.
            - Pooled Text Top-Up → NEVER EXPIRES (owner decision 2026-08-13);
              durable until spent, and never a `workspace_credits` row at all.
          Do not re-collapse these into one blanket line. The pooled branch was
          previously "applies to your current billing cycle and does not carry
          over", which was true before the no-expiry decision and is now false. */}
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
        {pooled ? (
          <>
            Buy extra capacity when your monthly base allowance runs low. Voice and
            LiveAvatar credits expire 12 months from purchase and are consumed
            oldest-first, and LiveAvatar minutes last only while your Live Avatar
            plan is active; AI text top-ups never expire and stay on your
            workspace until you use them.
          </>
        ) : (
          <>
            Buy extra credits when your monthly base allowance runs low. Credits expire
            12 months from purchase and are consumed oldest-first.
          </>
        )}
      </p>

      {/* Bundle cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => {
          // Owner check FIRST, matching the endpoint's own order
          // (`_require_owner` then the entitlement ladder): a non-owner without
          // the add-on should be told the thing they can actually act on.
          const needsAvatarAddon = c.kind === 'liveavatar' && !canBuyAvatarBundle;
          const disabled = !isOwner || needsAvatarAddon;
          const disabledReason = !isOwner
            ? 'Only workspace owners can purchase bundles'
            : needsAvatarAddon
            ? 'Requires an active Live Avatar plan'
            : '';
          const buttonLabel = !isOwner
            ? 'Owner only'
            : needsAvatarAddon
            ? 'Requires Live Avatar plan'
            : `Buy ${c.title}`;
          return (
            <div
              key={c.kind}
              className="relative p-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary/50 transition-all flex flex-col"
            >
              <div
                className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.accent} flex items-center justify-center text-white mb-3`}
              >
                {c.icon}
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white">{c.title}</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {c.price}
                <span className="text-xs font-medium text-gray-500 ml-1">one-time</span>
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 mb-4 leading-relaxed flex-1">
                {c.oneLiner}
              </p>
              <button
                onClick={() => setActiveModal(c.kind)}
                disabled={disabled}
                className={`w-full py-2 rounded-lg text-sm font-semibold transition-all ${
                  disabled
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary to-purple-600 text-white hover:shadow-lg'
                }`}
                title={disabledReason}
              >
                {buttonLabel}
              </button>
              {/* Disabled-with-explanation, not hidden: a vanished card teaches
                  nothing, and "why can't I buy this" is the question a customer
                  without the add-on actually has. Rendered as text rather than
                  left to the `title` tooltip, which does not exist on touch. */}
              {needsAvatarAddon && isOwner && (
                <p className="mt-2 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                  Requires an active Live Avatar plan — subscribe above, then buy
                  minutes.
                </p>
              )}
              {/* Owner ruling 2026-08-20: bundle minutes are forfeited when the
                  add-on ends. Said once, plainly, before the money is spent. */}
              {c.kind === 'liveavatar' && !needsAvatarAddon && (
                <p className="mt-2 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                  Minutes can only be used while your Live Avatar plan is active.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Active credits panel */}
      <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Active credits
          </h3>
          {activeCredits.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {activeCredits.length} active row{activeCredits.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {/* 🚨 THE FIX. A paid, positive top-up balance is stated HERE, in the
            panel that used to deny it, rather than only two sections away.
            Deliberately a compact strip and not a second big balance card:
            the AI Text Top-Ups section directly below already owns the
            headline figure, and two large emerald boxes stacked read as two
            different balances. Same number, stated once loudly and once
            quietly, in the two places a customer looks. */}
        {hasTopUpBalance && (
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2.5 mb-3 flex items-start gap-2">
            <Wallet className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm text-emerald-900 dark:text-emerald-100">
                <span className="font-semibold tabular-nums">
                  {formatTokenCount(topUpBalance)}
                </span>{' '}
                AI text top-up tokens remaining
              </p>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-0.5">
                Top-ups go straight into your text pool and never expire, so
                they aren't listed as expiring credits below. See AI Text
                Top-Ups for the purchases behind this balance.
              </p>
            </div>
          </div>
        )}
        {activeCredits.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-start gap-2 py-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {/* 🚨 Never "no credits, buy a bundle" while money is sitting in
                the pool — that sentence is what made the owner conclude a
                successful purchase had failed.

                🚨 A ZERO balance in pooled mode deliberately keeps the generic
                sentence. `text_tokens_topup === 0` cannot tell "bought one and
                spent it" apart from "never bought one", so the copy has to be
                true of both — and it is: they hold nothing, and buying more is
                the correct next step. It denies no purchase, because the
                receipt for a spent top-up is still listed (with a "Spent"
                badge) in AI Text Top-Ups directly below. Do not reword this
                into "your top-up is used up" — that would be a false claim on
                a workspace that never bought one. */}
            {hasTopUpBalance
              ? 'No expiring credits. Voice and LiveAvatar credits appear here; your top-up balance above is separate and stays until you use it.'
              : 'No active credits. Buy a bundle above to extend your allowance.'}
          </p>
        ) : (
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 px-3">Resource</th>
                  <th className="py-2 px-3 text-right">Remaining</th>
                  <th className="py-2 px-3 text-right">Initial</th>
                  <th className="py-2 px-3">Bundle</th>
                  <th className="py-2 px-3">Expires</th>
                </tr>
              </thead>
              <tbody>
                {activeCredits.map((c, i) => (
                  <tr
                    key={`${c.resource}-${c.purchased_at}-${i}`}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300 font-medium">
                      {RESOURCE_LABEL[c.resource] ?? c.resource}
                    </td>
                    <td className="py-2.5 px-3 text-right text-gray-900 dark:text-white font-semibold tabular-nums">
                      {formatCreditAmount(c)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                      {formatCreditInitial(c)}
                    </td>
                    <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300">
                      {BUNDLE_TYPE_LABEL[c.bundle_type] ?? c.bundle_type}
                    </td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(c.expires_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {activeModal === 'text' && (
        <TextBundleModal workspaceId={workspaceId} onClose={() => setActiveModal(null)} />
      )}
      {activeModal && activeModal !== 'text' && (
        <SimpleBundleModal
          kind={activeModal as SimpleBundleKind}
          workspaceId={workspaceId}
          onClose={() => setActiveModal(null)}
        />
      )}
    </section>
  );
};

export default BundleCards;
